import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Card, CardBody, CardHeader, Button, ButtonGroup,
  Row, Col, Container, Spinner, Form, FormGroup, Label, Input,
  Table
} from 'reactstrap';
import { format, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear, endOfYear, parseISO, formatISO, parse } from 'date-fns';
import { fr } from 'date-fns/locale';
import { saleActions } from '../../sagas/saleSlice';
import { FaCalendarAlt, FaDownload, FaUser, FaFileAlt, FaPrint } from 'react-icons/fa';
import { convertNumberToWords, formatPrice } from '../../helpers/utility';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { useReactToPrint } from 'react-to-print';
import { clientActions } from '../../sagas/clientSlice';
import Select from "react-select";
import autoTable from 'jspdf-autotable';

const VentesStatistiques = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { company } = useSelector(state => state.auth);
  const [blFilter, setBlFilter] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [chantierFilter, setChantierFilter] = useState('');
  const [designationFilter, setDesignationFilter] = useState('');
  const now = new Date();
  const todayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrowDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  const pad = (n) => n.toString().padStart(2, '0');
  const formatDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

  const [filters, setFilters] = useState({
    clientId: null,
    chantierId: null,
    dateDebut: formatDate(todayDate),
    dateFin: formatDate(tomorrowDate),
    query: ''
  });

  useEffect(() => {
    dispatch(saleActions.clearClientReport());
  }, []);

  const parseDate = (dateString) => {
    if (!dateString) return new Date();

    if (dateString.includes('-')) {
      return parseISO(dateString);
    }

    return parse(dateString, 'dd/MM/yyyy', new Date());
  };

  // Client report state
  const { clients, loading } = useSelector(state => state.clients);
  const { clientReport, clientReportLoading, clientReportError } = useSelector(state => state.sales);
  const [selectedClient, setSelectedClient] = useState('');
  const clientReportRef = useRef(null);

  const clientOptions = clients?.map(client => ({
    value: client.id,
    label: client.typeClient === 'PARTICULIER'
      ? `${client.nom} ${client.prenom || ''} `
      : client.entreprise,
    chantiers: client.chantiers || []
  })) ?? [];

  const clientOptionsWithAll = [
    { value: null, label: t('common.all', 'Tous') },
    ...clientOptions
  ];

  const chantierOptions = selectedClient?.chantiers?.map(chantier => ({
    value: chantier.id,
    label: `${chantier.code} - ${chantier.nom}`,
    ...chantier
  })) || [];

  const chantierOptionsWithAll = [
    { value: null, label: t('common.all', 'Tous') },
    ...chantierOptions
  ];

  // Effect for fetching data
  useEffect(() => {
    if (clients.length === 0) {
      // Fetch clients for client report
      const fetchClients = async () => {
        dispatch(clientActions.search({
          query: ""
        }));
      };

      fetchClients();
    }
  }, [dispatch]);


  // Generate client report
  const generateClientReport = () => {
    dispatch(saleActions.generateClientReport({ ...filters }));
  };

  // Print client report
  const handlePrintClientReport = useReactToPrint({
    content: () => clientReportRef.current,
    documentTitle: 'Rapport_Detail_Vente_Client',
    pageStyle: '@page { size: landscape; }',
  });

  const exportClientReportToPDF = () => {
    if (!clientReport) return;

    // Create PDF in landscape mode
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });

    // Page dimensions
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 10;

    // Define the footer height to reserve space at bottom of each page
    const footerHeight = 15;

    // Enhanced color scheme
    const primaryColor = [41, 128, 185]; // Blue
    const secondaryColor = [52, 152, 219]; // Lighter blue
    const accentColor = [39, 174, 96]; // Green for positive values
    const negativeColor = [231, 76, 60]; // Red for negative values
    const lightGray = [245, 245, 245]; // Background for alternating rows

    // Function to add header to each page
    const addPageHeader = () => {
      // Header with gradient-like effect
      doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
      doc.rect(0, 0, pageWidth, 30, 'F');
      doc.setFillColor(secondaryColor[0], secondaryColor[1], secondaryColor[2]);
      doc.rect(0, 25, pageWidth, 5, 'F');

      // Document title with improved typography
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(22);
      doc.setFont('helvetica', 'bold');
      doc.text('RAPPORT VENTE CLIENT', pageWidth / 2, 15, { align: 'center' });

      // Period with better spacing
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      const formattedDateFrom = format(parseDate(clientReport.period.from), 'dd/MM/yyyy', { locale: fr });
      const formattedDateTo = format(parseDate(clientReport.period.to), 'dd/MM/yyyy', { locale: fr });

      // Add chantier info to period line if specific chantier is selected
      const selectedChantierInfo = filters.chantierId && selectedClient?.chantiers
        ? selectedClient.chantiers.find(ch => ch.id === filters.chantierId)
        : null;

      let periodText = `Période: du ${formattedDateFrom} au ${formattedDateTo}`;
      if (selectedChantierInfo) {
        periodText += ` - Chantier: ${selectedChantierInfo.code}`;
      }

      doc.text(periodText, pageWidth / 2, 22, { align: 'center' });
    };

    // Function to add footer to each page
    const addPageFooter = () => {
      const footerY = pageHeight - footerHeight;
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.3);
      doc.line(margin, footerY, pageWidth - margin, footerY);

      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      doc.text(`Document généré le: ${clientReport.generationDate}`, pageWidth - margin, footerY + 5, { align: 'right' });
      doc.text(company.name, margin, footerY + 5);
      doc.text(`Page ${doc.internal.getNumberOfPages()}`, pageWidth / 2, footerY + 5, { align: 'center' });
    };

    // Function to format amount with sign and color
    const formatAmountWithSign = (amount) => {
      const numAmount = parseFloat(amount);
      if (isNaN(numAmount)) return formatPrice(0, false);

      if (numAmount > 0) {
        return `+${formatPrice(numAmount, false)}`;
      } else if (numAmount < 0) {
        // For negative values, we want to show the minus sign
        // but formatPrice already includes it for negative numbers
        return formatPrice(numAmount, false);
      } else {
        return formatPrice(0, false);
      }
    };

    // Add first page header
    addPageHeader();

    // Company information with logo placeholder
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(company.name, margin, 40);

    // Add a decorative line under company name
    doc.setDrawColor(primaryColor[0], primaryColor[1], primaryColor[2]);
    doc.setLineWidth(0.5);
    doc.line(margin, 42, margin + 60, 42);

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(`Adresse: ${company.address}`, margin, 47);
    doc.text(`Tél: ${company.phone1} | Email: ${company.email}`, margin, 52);
    doc.text(`RC: ${company.rc} | IF: ${company.identificationFiscale} | ICE: ${company.ice}`, margin, 57);

    // Client information in a styled box
    doc.setFillColor(248, 249, 250);
    doc.setDrawColor(220, 220, 220);

    // Determine chantier info for display
    const selectedChantierInfo = filters.chantierId && selectedClient?.chantiers
      ? selectedClient.chantiers.find(ch => ch.id === filters.chantierId)
      : null;

    // Adjust box height and position based on whether chantier info is present
    const clientBoxHeight = selectedChantierInfo ? 40 : 30;
    const clientBoxY = selectedChantierInfo ? 40 : 35; // Add extra space when chantier exists
    doc.roundedRect(pageWidth - margin - 80, clientBoxY, 80, clientBoxHeight, 2, 2, 'FD');

    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('INFORMATIONS CLIENT', pageWidth - margin - 75, clientBoxY + 7);

    // Add a decorative line under client info title
    doc.setDrawColor(accentColor[0], accentColor[1], accentColor[2]);
    doc.line(pageWidth - margin - 75, clientBoxY + 9, pageWidth - margin - 15, clientBoxY + 9);

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(`Client: ${clientReport.client.name}`, pageWidth - margin - 75, clientBoxY + 15);
    doc.text(`Téléphone: ${clientReport.client.phone}`, pageWidth - margin - 75, clientBoxY + 20);
    doc.text(`Code client: ${clientReport.client.code}`, pageWidth - margin - 75, clientBoxY + 25);

    // Add chantier information if a specific chantier is selected
    if (selectedChantierInfo) {
      doc.setFont('helvetica', 'bold');
      doc.text('Chantier:', pageWidth - margin - 75, clientBoxY + 32);
      doc.setFont('helvetica', 'normal');
      doc.text(`${selectedChantierInfo.code} - ${selectedChantierInfo.nom}`, pageWidth - margin - 75, clientBoxY + 37);
    }

    // Delivery notes table data preparation
    // Determine if we need to include chantier column (when no specific chantier is selected)
    const includeChantierColumn = !selectedChantierInfo;
    const tableHeaders = includeChantierColumn 
      ? ['N° BL', 'Date', 'Chantier', 'Désignation', 'Quantité', 'UM', 'PU', 'Total']
      : ['N° BL', 'Date', 'Désignation', 'Quantité', 'UM', 'PU', 'Total'];

    const tableData = clientReport.deliveryNotes.map(note => {
      const baseData = [
        note.blNumber,
        format(parseDate(note.date), 'dd/MM/yyyy', { locale: fr })
      ];
      
      if (includeChantierColumn) {
        baseData.push(note.chantier ? `${note.chantier.code}` : 'N/A');
      }
      
      baseData.push(
        note.designation,
        note.quantity.toString(),
        note.unit,
        formatPrice(note.unitPrice, false),
        formatPrice(note.lineTotal, false)
      );
      
      return baseData;
    });

    // Calculate column widths for full-width table
    const tableWidth = pageWidth - (2 * margin);
    
    let colWidth1, colWidth2, colWidth3, colWidth4, colWidth5, colWidth6, colWidth7, colWidth8;
    
    if (includeChantierColumn) {
      // With chantier column (8 columns total)
      colWidth1 = tableWidth * 0.09; // N° BL
      colWidth2 = tableWidth * 0.09; // Date  
      colWidth3 = tableWidth * 0.12; // Chantier
      colWidth4 = tableWidth * 0.32; // Désignation (largest column)
      colWidth5 = tableWidth * 0.08; // Quantité
      colWidth6 = tableWidth * 0.08; // UM
      colWidth7 = tableWidth * 0.11; // PU
      colWidth8 = tableWidth * 0.11; // Total
    } else {
      // Without chantier column (7 columns total)
      colWidth1 = tableWidth * 0.1; // N° BL
      colWidth2 = tableWidth * 0.1;  // Date
      colWidth3 = tableWidth * 0.38;  // Désignation (largest column)
      colWidth4 = tableWidth * 0.08; // Quantité
      colWidth5 = tableWidth * 0.08; // UM
      colWidth6 = tableWidth * 0.13; // PU
      colWidth7 = tableWidth * 0.13; // Total
    }

    // Format dates for summary
    const formattedDateFrom = format(parseDate(clientReport.period.from), 'dd/MM/yyyy', { locale: fr });
    const formattedDateTo = format(parseDate(clientReport.period.to), 'dd/MM/yyyy', { locale: fr });

    // Pagination logic
    const totalRows = tableData.length;
    let currentRow = 0;

    // First page - always 8 rows
    const firstPageRows = Math.min(8, totalRows);
    const firstPageData = tableData.slice(0, firstPageRows);
    currentRow += firstPageRows;

    // Add first page table
    const tableStartY = selectedChantierInfo ? 80 : 70; // Add extra space when chantier exists
    autoTable(doc, {
      startY: tableStartY,
      head: [tableHeaders],
      body: firstPageData,
      theme: 'grid',
      headStyles: {
        fillColor: primaryColor,
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        halign: 'center',
        fontSize: 10
      },
      columnStyles: includeChantierColumn ? {
        0: { halign: 'center', cellWidth: colWidth1 },
        1: { halign: 'center', cellWidth: colWidth2 },
        2: { halign: 'center', cellWidth: colWidth3 },
        3: { halign: 'left', cellWidth: colWidth4 },
        4: { halign: 'center', cellWidth: colWidth5 },
        5: { halign: 'center', cellWidth: colWidth6 },
        6: { halign: 'right', cellWidth: colWidth7 },
        7: { halign: 'right', cellWidth: colWidth8 }
      } : {
        0: { halign: 'center', cellWidth: colWidth1 },
        1: { halign: 'center', cellWidth: colWidth2 },
        2: { halign: 'left', cellWidth: colWidth3 },
        3: { halign: 'center', cellWidth: colWidth4 },
        4: { halign: 'center', cellWidth: colWidth5 },
        5: { halign: 'right', cellWidth: colWidth6 },
        6: { halign: 'right', cellWidth: colWidth7 }
      },
      alternateRowStyles: {
        fillColor: lightGray
      },
      styles: {
        overflow: 'linebreak',
        cellPadding: 3,
        fontSize: 9
      },
      margin: { top: margin, right: margin, bottom: footerHeight, left: margin },
      didDrawPage: () => {
        addPageFooter();
      }
    });

    // Calculate remaining rows
    const remainingRows = totalRows - currentRow;

    // If we have more rows to display
    if (remainingRows > 0) {
      // Calculate how many middle pages with 12 rows each
      const middlePageCount = Math.floor(remainingRows / 12);

      // Add middle pages with 12 rows each
      for (let i = 0; i < middlePageCount; i++) {
        doc.addPage();
        addPageHeader();

        const pageData = tableData.slice(currentRow, currentRow + 12);
        currentRow += 12;

        autoTable(doc, {
          startY: 40, // Start after header
          head: [tableHeaders],
          body: pageData,
          theme: 'grid',
          headStyles: {
            fillColor: primaryColor,
            textColor: [255, 255, 255],
            fontStyle: 'bold',
            halign: 'center',
            fontSize: 10
          },
          columnStyles: includeChantierColumn ? {
            0: { halign: 'center', cellWidth: colWidth1 },
            1: { halign: 'center', cellWidth: colWidth2 },
            2: { halign: 'center', cellWidth: colWidth3 },
            3: { halign: 'left', cellWidth: colWidth4 },
            4: { halign: 'center', cellWidth: colWidth5 },
            5: { halign: 'center', cellWidth: colWidth6 },
            6: { halign: 'right', cellWidth: colWidth7 },
            7: { halign: 'right', cellWidth: colWidth8 }
          } : {
            0: { halign: 'center', cellWidth: colWidth1 },
            1: { halign: 'center', cellWidth: colWidth2 },
            2: { halign: 'left', cellWidth: colWidth3 },
            3: { halign: 'center', cellWidth: colWidth4 },
            4: { halign: 'center', cellWidth: colWidth5 },
            5: { halign: 'right', cellWidth: colWidth6 },
            6: { halign: 'right', cellWidth: colWidth7 }
          },
          alternateRowStyles: {
            fillColor: lightGray
          },
          styles: {
            overflow: 'linebreak',
            cellPadding: 3,
            fontSize: 9
          },
          margin: { top: margin, right: margin, bottom: footerHeight, left: margin },
          didDrawPage: () => {
            addPageFooter();
          }
        });
      }

      // Check if we have any remaining rows for the last page
      const lastPageRows = remainingRows % 12;

      if (lastPageRows > 0) {
        doc.addPage();
        addPageHeader();

        const lastPageData = tableData.slice(currentRow);

        const footerRow = includeChantierColumn 
          ? ['', '', '', '', '', '', 'Total:', formatPrice(clientReport.summary.totalConsome, false)]
          : ['', '', '', '', '', 'Total:', formatPrice(clientReport.summary.totalConsome, false)];

        autoTable(doc, {
          startY: 40, // Start after header
          head: [tableHeaders],
          body: lastPageData,
          foot: [footerRow],
          theme: 'grid',
          headStyles: {
            fillColor: primaryColor,
            textColor: [255, 255, 255],
            fontStyle: 'bold',
            halign: 'center',
            fontSize: 10
          },
          footStyles: {
            fillColor: [240, 240, 240],
            textColor: [0, 0, 0],
            fontStyle: 'bold',
            fontSize: 10
          },
          columnStyles: includeChantierColumn ? {
            0: { halign: 'center', cellWidth: colWidth1 },
            1: { halign: 'center', cellWidth: colWidth2 },
            2: { halign: 'center', cellWidth: colWidth3 },
            3: { halign: 'left', cellWidth: colWidth4 },
            4: { halign: 'center', cellWidth: colWidth5 },
            5: { halign: 'center', cellWidth: colWidth6 },
            6: { halign: 'right', cellWidth: colWidth7 },
            7: { halign: 'right', cellWidth: colWidth8 }
          } : {
            0: { halign: 'center', cellWidth: colWidth1 },
            1: { halign: 'center', cellWidth: colWidth2 },
            2: { halign: 'left', cellWidth: colWidth3 },
            3: { halign: 'center', cellWidth: colWidth4 },
            4: { halign: 'center', cellWidth: colWidth5 },
            5: { halign: 'right', cellWidth: colWidth6 },
            6: { halign: 'right', cellWidth: colWidth7 }
          },
          alternateRowStyles: {
            fillColor: lightGray
          },
          styles: {
            overflow: 'linebreak',
            cellPadding: 3,
            fontSize: 9
          },
          margin: { top: margin, right: margin, bottom: footerHeight, left: margin },
          didDrawPage: () => {
            addPageFooter();
          }
        });
      } else {
        // If we don't have any rows for the last page (exactly divisible by 12),
        // add the total row to the last table we created
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        const totalColPosition = includeChantierColumn ? colWidth8 : colWidth7;
        const totalLabelPosition = includeChantierColumn ? (colWidth7 + colWidth8) : colWidth7;
        doc.text('Total:', pageWidth - margin - totalLabelPosition - 5, doc.lastAutoTable.finalY + 8, { align: 'right' });
        doc.text(formatPrice(clientReport.summary.totalConsome, false), pageWidth - margin, doc.lastAutoTable.finalY + 8, { align: 'right' });
      }
    } else {
      // If all data fits on the first page, add the total row
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      const totalColPosition = includeChantierColumn ? colWidth8 : colWidth7;
      const totalLabelPosition = includeChantierColumn ? (colWidth7 + colWidth8) : colWidth7;
      doc.text('Total:', pageWidth - margin - totalLabelPosition - 5, doc.lastAutoTable.finalY + 8, { align: 'right' });
      doc.text(formatPrice(clientReport.summary.totalConsome, false), pageWidth - margin, doc.lastAutoTable.finalY + 8, { align: 'right' });
    }

    // Get the Y position after the table
    let summaryY = doc.lastAutoTable.finalY + 15;

    // Check if we have enough space for summary section (at least 70mm)
    // If not, add a new page
    if (summaryY > pageHeight - 80) {
      doc.addPage();
      addPageHeader();
      summaryY = 40; // Start after header on new page
    }

    // Summary section with styled box - left side
    doc.setFillColor(248, 249, 250);
    doc.roundedRect(margin, summaryY, pageWidth * 0.3, 50, 2, 2, 'F');

    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2]);
    doc.text('RÉSUMÉ', margin + 5, summaryY + 8);

    // Add a decorative line under summary title
    doc.setDrawColor(primaryColor[0], primaryColor[1], primaryColor[2]);
    doc.line(margin + 5, summaryY + 10, margin + 35, summaryY + 10);

    doc.setTextColor(0, 0, 0);

    // Prepare data for summary table
    // We'll handle the Net A Payer row separately for color formatting
    const summaryData = [
      ['Montant Total Consommé', formatPrice(clientReport.summary.totalConsome, false)],
      [`Solde Avant le ${formattedDateFrom}`, formatPrice(clientReport.summary.soldeAvant, false)],
      ['Avance', formatPrice(clientReport.summary.avance, false)],
      ['Avoir', formatPrice(clientReport.summary.avoir, false)]
    ];

    // Summary table with better styling (without Net A Payer row)
    autoTable(doc, {
      startY: summaryY + 15,
      body: summaryData,
      theme: 'plain',
      styles: {
        cellPadding: 3,
        fontSize: 10
      },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: pageWidth * 0.2 },
        1: { halign: 'right', cellWidth: pageWidth * 0.1 }
      },
      tableWidth: pageWidth * 0.3
    });

    // Get the Y position after the summary table
    const netAPayerY = doc.lastAutoTable.finalY + 5;

    // Add the Net A Payer row with color based on value
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Net A Payer', margin + 5, netAPayerY);

    // Determine color and format for Net A Payer amount
    const netAPayerAmount = parseFloat(clientReport.summary.netApaye);
    if (netAPayerAmount > 0) {
      // Positive amount - green with + sign
      doc.setTextColor(accentColor[0], accentColor[1], accentColor[2]);
      doc.text(`+${formatPrice(netAPayerAmount, false)}`, margin + pageWidth * 0.3 - 5, netAPayerY, { align: 'right' });
    } else if (netAPayerAmount < 0) {
      // Negative amount - red (minus sign already included in formatPrice)
      doc.setTextColor(negativeColor[0], negativeColor[1], negativeColor[2]);
      doc.text(formatPrice(netAPayerAmount, false), margin + pageWidth * 0.3 - 5, netAPayerY, { align: 'right' });
    } else {
      // Zero amount - neutral color
      doc.setTextColor(0, 0, 0);
      doc.text(formatPrice(0, false), margin + pageWidth * 0.3 - 5, netAPayerY, { align: 'right' });
    }

    // Reset text color for subsequent content
    doc.setTextColor(0, 0, 0);

    // Amount in words in a styled box - right side
    const amountY = summaryY;
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(accentColor[0], accentColor[1], accentColor[2]);
    doc.roundedRect(pageWidth - margin - (pageWidth * 0.4), amountY, pageWidth * 0.4, 40, 2, 2, 'FD');

    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(accentColor[0], accentColor[1], accentColor[2]);
    doc.text('Arrêté le présent document à la somme de:', pageWidth - margin - (pageWidth * 0.38), amountY + 10);

    // Set color for amount in words based on value
    if (netAPayerAmount < 0) {
      doc.setTextColor(negativeColor[0], negativeColor[1], negativeColor[2]);
    } else {
      doc.setTextColor(accentColor[0], accentColor[1], accentColor[2]);
    }

    doc.setFont('helvetica', 'italic');
    // Use absolute value for amount in words, but indicate negative if needed
    const amountWords = netAPayerAmount < 0
      ? `Moins ${convertNumberToWords(Math.abs(netAPayerAmount))}`
      : convertNumberToWords(netAPayerAmount);
    doc.text(`${amountWords} Dirhams`,
      pageWidth - margin - (pageWidth * 0.38), amountY + 20,
      { maxWidth: pageWidth * 0.36 });

    // Note with a subtle background - positioned below amount in words
    doc.setFillColor(245, 245, 245);
    doc.roundedRect(pageWidth - margin - (pageWidth * 0.4), amountY + 45, pageWidth * 0.4, 15, 2, 2, 'F');

    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text(`NB: Montant Total Consommé sur la période du ${formattedDateFrom} au ${formattedDateTo}`,
      pageWidth - margin - (pageWidth * 0.38), amountY + 52,
      { maxWidth: pageWidth * 0.36 });

    // Add footer to the last page
    addPageFooter();

    // Save the PDF with a well-formatted filename
    const sanitizedClientName = clientReport.client.name.replace(/[^a-z0-9]/gi, '_').toLowerCase();
    doc.save(`rapport_vente_${sanitizedClientName}_${format(new Date(), 'yyyyMMdd')}.pdf`);
  };


  // Filter states for BL number and designation


  // Filtered delivery notes based on BL number, client, chantier, and designation
  let filteredDeliveryNotes = [];
  if (clientReport && clientReport.deliveryNotes) {
    filteredDeliveryNotes = clientReport.deliveryNotes.filter(note => {
      const blMatch = blFilter ? (note.blNumber || '').toUpperCase().includes(blFilter) : true;
      const clientMatch = clientFilter ? (note.client?.name || '').toUpperCase().includes(clientFilter) : true;
      const chantierMatch = chantierFilter ? (note.chantier?.nom || '').toUpperCase().includes(chantierFilter) : true;
      const designationMatch = designationFilter ? (note.designation || '').toUpperCase().includes(designationFilter) : true;
      return blMatch && clientMatch && chantierMatch && designationMatch;
    });
  }

  return (
    <Container fluid className="page-content">
      <Card className="shadow-sm mb-4">
        <CardHeader className="bg-white">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <FaFileAlt className="me-2 text-primary" />
              Détails des ventes
            </h5>
          </div>
        </CardHeader>
        <CardBody>
          <Row className="mb-4">
            <Col md={3}>
              <FormGroup>
                <Label for="clientSelect" className="form-label">
                  <FaUser className="me-1" /> Client
                </Label>
                <Select
                  id="clientId"
                  options={clientOptionsWithAll}
                  value={clientOptionsWithAll.find(option => option.value === filters.clientId)}
                  onChange={(selectedOption) => {
                    setFilters(f => ({ ...f, clientId: selectedOption ? selectedOption.value : null }));
                    setSelectedClient(selectedOption);
                  }}
                  placeholder={t('bonLivraison.selectClient')}
                  isSearchable
                  menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                  styles={{
                    menuPortal: base => ({ ...base, zIndex: 9999 })
                  }}
                />
              </FormGroup>
            </Col>
            
            <Col md={3}>
              <FormGroup>
                <Label for="startDate" className="form-label">
                  <FaCalendarAlt className="me-1" /> Date de début
                </Label>
                <Input
                  type="date"
                  id="dateDebut"
                  value={filters.dateDebut}
                  onChange={e => setFilters(f => ({ ...f, dateDebut: e.target.value }))}
                />
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup>
                <Label for="endDate" className="form-label">
                  <FaCalendarAlt className="me-1" /> Date de fin
                </Label>
                <Input
                  type="date"
                  id="dateFin"
                  value={filters.dateFin}
                  onChange={e => setFilters(f => ({ ...f, dateFin: e.target.value }))}
                />
              </FormGroup>
            </Col>
            <Col md={3} className="d-flex mb-3 align-items-end">
              <Button
                color="success"
                className="me-2"
                size='sm'
                onClick={generateClientReport}
                disabled={clientReportLoading}
              >
                {clientReportLoading ? (
                  <>
                    <Spinner size="sm" className="me-1" /> Génération...
                  </>
                ) : (
                  <>
                    <FaFileAlt className="me-1" /> Générer
                  </>
                )}
              </Button>
              {clientReport && clientReport.client && (
                <>
                  <Button
                    color="secondary"
                    size='sm'
                    onClick={exportClientReportToPDF}
                  >
                    <FaDownload className="me-1" /> Télécharger
                  </Button>
                </>
              )}
            </Col>
          </Row>

          {/* Client Report Content */}
          <div ref={clientReportRef}>
            {clientReport ? (
              <div className="client-report p-4 border rounded-4 bg-white shadow-sm animate__animated animate__fadeIn">
                {/* <div className="report-header mb-4 pb-2 border-bottom">
                  <div className="text-center mb-3">
                    <h4 className="text-primary mb-1 fw-bold letter-spacing-1">RAPPORT VENTE CLIENT</h4>
                    <p className="text-muted small mb-0">
                      <FaCalendarAlt className="me-1" />
                      Période: <span className="fw-semibold">du {format(parseDate(clientReport.period.from), 'dd/MM/yyyy', { locale: fr })} au {format(parseDate(clientReport.period.to), 'dd/MM/yyyy', { locale: fr })}</span>
                    </p>
                  </div>

                  <Row className="mb-3 g-4">
                    <Col md={6}>
                      <div className="company-info bg-light p-3 rounded-3 h-100">
                        <h5 className="mb-2 text-dark fw-bold"><FaFileAlt className="me-2 text-primary" />{company.name}</h5>
                        <div className="small text-secondary">
                          <div><span className="fw-semibold">Adresse:</span> {company.address}</div>
                          <div><span className="fw-semibold">Tél:</span> {company.phone1} <span className="mx-1">|</span> <span className="fw-semibold">Email:</span> {company.email}</div>
                          <div><span className="fw-semibold">RC:</span> {company.rc} <span className="mx-1">|</span> <span className="fw-semibold">IF:</span> {company.identificationFiscale} <span className="mx-1">|</span> <span className="fw-semibold">ICE:</span> {company.ice}</div>
                        </div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="client-info bg-white border border-2 border-primary p-3 rounded-3 h-100">
                        <h6 className="mb-2 text-primary fw-bold"><FaUser className="me-2" />Informations Client</h6>
                        <div className="small">
                          <div className="mb-1"><strong>Client:</strong> {clientReport.client.name}</div>
                          <div className="mb-1"><strong>Téléphone:</strong> {clientReport.client.phone}</div>
                          <div className="mb-0"><strong>Code client:</strong> {clientReport.client.code}</div>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </div> */}

                <div className="report-body mb-4">
                  <div className="table-responsive rounded-3 overflow-hidden">
                    {/* Filter fields for BL number and product designation */}
                    <div className="d-flex gap-3 mb-2 flex-wrap">
                      <Input
                        type="text"
                        placeholder="Filtrer par N° BL..."
                        value={blFilter || ''}
                        onChange={e => setBlFilter(e.target.value.toUpperCase())}
                        className="border border-2 rounded-3 px-3 text-uppercase fw-bold"
                        style={{ maxWidth: 160 }}
                      />
                      <Input
                        type="text"
                        placeholder="Filtrer par Client..."
                        value={clientFilter || ''}
                        onChange={e => setClientFilter(e.target.value.toUpperCase())}
                        className="border border-2 rounded-3 px-3 text-uppercase fw-bold"
                        style={{ maxWidth: 180 }}
                      />
                     
                      <Input
                        type="text"
                        placeholder="Filtrer par Désignation..."
                        value={designationFilter || ''}
                        onChange={e => setDesignationFilter(e.target.value.toUpperCase())}
                        className="border border-2 rounded-3 px-3 text-uppercase fw-bold"
                        style={{ maxWidth: 200 }}
                      />
                    </div>
                    <Table bordered hover size="sm" className="mb-0 align-middle">
                      <thead className="table-primary">
                        <tr>
                          <th className="text-center">N° BL</th>
                          <th className="text-center">Client</th>
                          <th className="text-center">Date</th>
                          <th className="text-center">Désignation</th>
                          <th className="text-center">Quantité</th>
                          <th className="text-center">UM</th>
                          <th className="text-end">PU</th>
                          <th className="text-end">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredDeliveryNotes.map((note, index) => (
                          <tr key={note.id} className={index % 2 === 0 ? "bg-light" : ""}>
                            <td className="text-center fw-semibold">{note.blNumber}</td>
                            <td className="text-center">{note.client.name}</td>
                            <td className="text-center">{format(parseDate(note.date), 'dd/MM/yyyy', { locale: fr })}</td>
                            <td className="ps-2">{note.designation}</td>
                            <td className="text-center">{note.quantity}</td>
                            <td className="text-center">{note.unit}</td>
                            <td className="text-end">{formatPrice(note.unitPrice, false)}</td>
                            <td className="text-end fw-bold">{formatPrice(note.lineTotal, false)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="table-light">
                        <tr>
                          <td colSpan="4" className="text-end fw-bold">Total:</td>
                          <td className="text-center fw-bold text-primary">
                            {filteredDeliveryNotes.reduce((acc, note) => acc + (Number(note.quantity) || 0), 0)}
                          </td>
                          <td></td>
                          <td></td>
                          <td className="text-end fw-bold text-primary">{formatPrice(filteredDeliveryNotes.reduce((acc, note) => acc + (note.lineTotal || 0), 0), false)}</td>
                        </tr>
                      </tfoot>
                    </Table>

                  </div>
                </div>


                { clientReport && clientReport.summary && <div className="report-summary mb-4">
                  <Row className="g-4">
                    <Col md={6}>
                      <div className="mb-3 bg-light p-3 rounded-3 border border-1 border-secondary">
                        <h6 className="border-bottom pb-2 mb-3 text-secondary fw-bold">Résumé</h6>
                        <Table borderless size="sm" className="summary-table">
                          <tbody>
                            <tr>
                              <td className="fw-bold">Montant Total Consommé</td>
                              <td className="text-end">{formatPrice(clientReport.summary.totalConsome || 0, false)}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Solde Avant le {format(parseDate(clientReport.period.from), 'dd/MM/yyyy', { locale: fr })}</td>
                              <td className="text-end">{formatPrice(clientReport.summary.soldeAvant || 0, false)}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Avance</td>
                              <td className="text-end">{formatPrice(clientReport.summary.avance || 0, false)}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Règlements normaux (hors avances)</td>
                              <td className="text-end">{formatPrice(clientReport.summary.reglements || 0, false)}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Avoir</td>
                              <td className="text-end">{formatPrice(clientReport.summary.avoir || 0, false)}</td>
                            </tr>
                            <tr className="table-light">
                              <td className="fw-bold">Net A Payer</td>
                              {
                                clientReport.summary.netApaye < 0 ? (
                                  <td className="text-end fw-bold text-danger">{formatPrice(clientReport.summary.netApaye, false)}</td>
                                ) : (
                                  <td className="text-end fw-bold text-success">{formatPrice(clientReport.summary.netApaye, false)}</td>
                                )
                              }
                            </tr>
                          </tbody>
                        </Table>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="amount-in-words p-4 bg-white rounded-3 border border-1 border-primary">
                        <p className="mb-1 fw-bold text-primary">Arrêté le présent document à la somme de:</p>
                        {
                          clientReport.summary.netApaye < 0 ?
                            < p className="fst-italic text-danger fs-5">
                              {`Moins ${convertNumberToWords(Math.abs(clientReport.summary.netApaye))} `}
                              Dirhams
                            </p> :
                            <p className="fst-italic text-success fs-5">
                              {`${convertNumberToWords(Math.abs(clientReport.summary.netApaye))} `}
                              Dirhams
                            </p>
                        }
                        <p className="mt-3 small text-muted">
                          <FaCalendarAlt className="me-1" />
                          NB: Montant Total Consommé sur la période du <span className="fw-semibold">{format(parseDate(clientReport.period.from), 'dd/MM/yyyy', { locale: fr })} au {format(parseDate(clientReport.period.to), 'dd/MM/yyyy', { locale: fr })}</span>
                        </p>


                      </div>
                    </Col>
                  </Row>
                </div>}

                <div className="report-footer mt-4 pt-3 border-top text-muted small">
                  <div className="d-flex justify-content-between align-items-center">
                    <span><FaCalendarAlt className="me-1" /> Document généré le: <span className="fw-semibold">{clientReport.generationDate}</span></span>
                    <span className="fw-bold">Page 1</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center p-5 bg-light rounded-4 border border-2 border-secondary animate__animated animate__fadeIn">
                {clientReportLoading ? (
                  <div>
                    <Spinner color="primary" />
                    <p className="mt-3">Génération du rapport en cours...</p>
                  </div>
                ) : (
                  <div>
                    <FaFileAlt size={48} className="text-muted mb-3" />
                    <h5 className="fw-bold">Aucun rapport généré</h5>
                    <p className="text-muted">
                      Veuillez sélectionner un client et cliquer sur <span className="fw-semibold">"Générer le rapport"</span> pour afficher les détails des ventes.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </CardBody>
      </Card>

      <style jsx>{`
        .client-report {
          font-size: 0.9rem;
        }
        .summary-table td {
          padding: 0.4rem 0.75rem;
        }
        .company-info h5 {
          color: #2c3e50;
        }
        .amount-in-words {
          border-left: 3px solid #3498db;
        }
        @media print {
          .page-content {
            padding: 0 !important;
          }
          .client-report {
            border: none !important;
          }
        }
      `}</style>
    </Container>
  );
};

export default VentesStatistiques;
